import { useEffect } from 'react';
import { Divider, Typography } from '@mui/material';
import { IosShare, Share } from '@mui/icons-material';
import { ROUTES } from 'constants/routes';
import { useStore } from 'providers';
import { useShare, useFetchFromService } from 'hooks';
import { pushAnalyticsEvent } from 'helpers/analytics';
import getUserDevice from 'utils/getUserDevice';
import Layout from 'layouts';
import { referAndEarnContent } from './ReferAndEarn.content';
import {
  Code,
  StyledBox,
  StyledContainer,
  StyledPaper,
  SubTitle,
  Text,
  Clipboard,
  Image,
  StyledBulletPointsList,
  StyledButton,
  Spinner,
} from './ReferAndEarn.styles';

const { howItWorks, title, subtitle } = referAndEarnContent;

function ReferAndEarn() {
  const { store } = useStore();
  const { referralCode: user } = store;
  const { referralCode } = user || {};

  const fetchReferralCode = useFetchFromService('referralCode');
  const userDevice = getUserDevice();

  const referAFriendURL = `${ROUTES.TERMS_AND_CONDITIONS}#referfriend`;

  useEffect(() => {
    if (!referralCode) {
      fetchReferralCode();
    }
  }, [referralCode, fetchReferralCode]);

  const { canShare, showShareSheet } = useShare({
    title: `Zillionaires' Club`,
    text: `Check out the Zillionaires' Club, the home of exclusive perkz like free food, drink and more! Join today and get a FREE perk on your next visit to Zizzi. Use my code ${referralCode}`,
    url: `${window.location.origin}${ROUTES.SIGNUP}?raf=${referralCode}`,
  });

  const ShareIcon = () => {
    if (!userDevice) return null;

    const shareIcon = {
      Android: <Share />,
      iOS: <IosShare />,
    };

    pushAnalyticsEvent({ event: 'copyReferralCode', title: `Share button ${userDevice}` });
    return shareIcon[userDevice];
  };

  const handleOnClick = () =>
    pushAnalyticsEvent({ event: 'copyReferralCode', title: 'Copy to Clipboard' });

  return (
    <Layout ariaLabel="Refer a friend">
      <StyledContainer>
        <StyledPaper>
          <Typography variant="h1">{title}</Typography>
          <Typography gutterBottom>{subtitle}</Typography>

          <Image />

          <SubTitle variant="h2" gutterBottom>
            {howItWorks.title}
          </SubTitle>

          <StyledBulletPointsList content={howItWorks.content} />

          <Divider />

          <StyledBox>
            {referralCode ? (
              <>
                <Typography variant="h4">{howItWorks.subtitle}</Typography>

                <Clipboard value={referralCode} onCopy={handleOnClick}>
                  <Code variant="h6">{referralCode}</Code>
                </Clipboard>

                {canShare && (
                  <>
                    <Typography variant="h4">Or</Typography>
                    <StyledButton onClick={showShareSheet} variant="contained">
                      Share a link
                      <ShareIcon />
                    </StyledButton>
                  </>
                )}
              </>
            ) : (
              <Spinner />
            )}
          </StyledBox>

          <Divider />

          <Text variant="subtitle1">
            Visit our <a href={ROUTES.HOW_IT_WORKS}>FAQs</a> for more information. By referring a
            friend, you agree to the <a href={referAFriendURL}>Terms & Conditions.</a>
          </Text>
        </StyledPaper>
      </StyledContainer>
    </Layout>
  );
}

export default ReferAndEarn;
